import { faPlusSquare, faSearch } from '@fortawesome/fontawesome-free-solid';
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { keyGen } from '../../../shared/firebase';
import { vals } from '../../../shared/helpers';
import { iList, iNote, UserAuth, iFullStoreState} from '../../../shared/interfaces';
import { equals } from 'ramda';
import { Fa } from "../../elements/fa";
import PerfectScrollbar from 'react-perfect-scrollbar';
import styles from './Notepad.module.scss';
import NoteItem from './NoteItem';
import * as notesdb from '../../../shared/db/notes-db';
import { getAllNoteFields, NoteFieldMap } from '../../../shared/db/note-extra-fields-db';
import NoteSearchModal from './NoteSearchModal';

type IProps = {
  ownerTitle?: string;
  notes: iList<iNote>;
  deviceOrFence: 'device' | 'fence';
  deviceOrFenceId: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  title?: string;
  cssOrder?: number;
  maxVisibleRows?: number;
};

const toNumberSafe = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const DEFAULT_VISIBLE_FIELDS = ['date', 'po', 'part', 'service', 'vendor', 'repairShop', 'note'];

const Notepad = ({
  notes,
  deviceOrFence,
  deviceOrFenceId,
  style = {},
  ownerTitle,
  disabled = false,
  cssOrder,
  maxVisibleRows
}: IProps) => {
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const [globalVisibleFields, setGlobalVisibleFields] = useState<string[]>(DEFAULT_VISIBLE_FIELDS);
  const [noteFieldDefs, setNoteFieldDefs] = useState<NoteFieldMap>({});
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [listMaxHeight, setListMaxHeight] = useState<number | undefined>(undefined);
  const notesListRef = useRef<HTMLDivElement | null>(null);

  const authUser = useSelector<iFullStoreState, UserAuth | undefined>(
    (state) => state.auth?.user ?? undefined,
    (l, r) => equals(l, r),
  );

  const toggleNote = useCallback((id: string) => {
    setOpenNoteId(prev => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fields, defs] = await Promise.all([
          notesdb.getGlobalVisibleFields ? notesdb.getGlobalVisibleFields() : Promise.resolve(null),
          getAllNoteFields().catch(() => ({}))
        ]);

        if (fields && Array.isArray(fields)) {
          // Check if any predefined fields are missing from saved fields
          const missingFields = DEFAULT_VISIBLE_FIELDS.filter(field => !fields.includes(field));
          if (missingFields.length > 0) {
            // Merge saved fields with new defaults to ensure new predefined fields are always included
            const mergedFields = [...new Set([...DEFAULT_VISIBLE_FIELDS, ...fields])].filter(value => !!value);
            setGlobalVisibleFields(mergedFields);

            // Save the updated fields back to the database
            if (authUser && notesdb.saveGlobalVisibleFields) {
              notesdb.saveGlobalVisibleFields(authUser)(mergedFields).catch(console.error);
            }
          } else {
            // No missing fields, use saved preferences as-is
            setGlobalVisibleFields(fields);
          }
        }

        if (defs) setNoteFieldDefs(defs);
      } catch (e) { console.error("Error loading note settings", e); }
    };
    fetchData();
  }, []);

  const notesArray = useMemo(() => {
    const arr = vals(notes);
    return arr.sort((a, b) => {
      const timeA = a?.date?.valueOf() ? moment(a.date).valueOf() : 0;
      const timeB = b?.date?.valueOf() ? moment(b.date).valueOf() : 0;
      return timeB - timeA;
    });
  }, [notes]);

  const totalSpent = useMemo(() => {
    return notesArray.reduce((sum, note) => sum + toNumberSafe((note as any)?.amount), 0);
  }, [notesArray]);

  useEffect(() => {
    if (!maxVisibleRows) {
      setListMaxHeight(undefined);
      return;
    }

    const measureHeight = () => {
      const firstNoteRow = notesListRef.current?.querySelector(`.${styles.customAccordionContainer}`) as HTMLElement | null;
      const measuredHeight = firstNoteRow?.offsetHeight || 24;
      setListMaxHeight(measuredHeight * maxVisibleRows);
    };

    measureHeight();

    window.requestAnimationFrame(measureHeight);
  }, [maxVisibleRows, notesArray.length]);

  const totalSpentText = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalSpent);

  return (
    <div style={{...style, '--cssOrder': cssOrder} as React.CSSProperties} className={styles.notepad}>
      <PerfectScrollbar>
        <div className={styles.notePad}>
          <div className={styles.totalSpentHeader}>
            <span className={styles.totalSpentLabel}>Total Spent:</span>
            <span className={styles.totalSpentValue}>{totalSpentText}</span>
          </div>

          <div className={`${styles.header} ${disabled ? styles.disabled : ''}`}>
            <Link
              className={`${styles.addNote} ${disabled ? styles.disabled : ''}`}
              to={{
                pathname: `/${deviceOrFence}/${deviceOrFenceId}/notes/add/${keyGen()}`,
                state: { ownerTitle },
              }}
            >
              <Fa icon={faPlusSquare} style={disabled ? { color: "gray" } : { color: "#ff7c02" }} />
              <span className={styles.addNoteText}>Note  |</span>
            </Link>

            <button
              className={styles.searchButtonTrigger}
              onClick={() => setIsSearchOpen(true)}
              disabled={disabled}
              title="Search Notes"
            >
              <Fa icon={faSearch}  style={disabled ? { color: "gray" } : { color: "#ff7c02" }} />
              <span className={styles.addNoteText}>Export</span>
            </button>
          </div>

          <div
            ref={notesListRef}
            className={maxVisibleRows ? styles.notesListScrollable : undefined}
            style={maxVisibleRows ? ({ maxHeight: listMaxHeight } as React.CSSProperties) : undefined}
          >
            {notesArray.length === 0 ? (
              <div className={styles.emptyState}>No notes added yet.</div>
            ) : (
              notesArray.map((note) => (
                <NoteItem
                  key={note.id}
                  note={note}
                  isOpen={openNoteId === note.id}
                  onToggle={toggleNote}
                  deviceOrFence={deviceOrFence}
                  deviceOrFenceId={deviceOrFenceId}
                  editLinkState={{ state: { ownerTitle } }}
                  disabled={disabled}
                  noteFieldDefs={noteFieldDefs}
                  globalVisibleFields={globalVisibleFields}
                />
              ))
            )}
          </div>
        </div>
      </PerfectScrollbar>

      {isSearchOpen && (
        <NoteSearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          currentNotes={notes}
          deviceOrFence={deviceOrFence}
          deviceOrFenceId={deviceOrFenceId}
          ownerTitle={ownerTitle}
          noteFieldDefs={noteFieldDefs}
          globalVisibleFields={globalVisibleFields}
        />
      )}
    </div>
  );
};

export default Notepad;